import { useCallback, useEffect, useRef, useState } from "react";

type SliderStatus = "idle" | "dragging" | "complete";

export function useOfferSlider(onComplete: () => void) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [slideX, setSlideX] = useState(0);
  const [status, setStatus] = useState<SliderStatus>("idle");
  const draggingRef = useRef(false);
  const [shimmer, setShimmer] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const slideXRef = useRef(0);

  const KNOB_W = 60;
  const COMPLETE_AT = 1; // require full completion

  const completedOnceRef = useRef(false);

  const beginSlide = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    setStatus("dragging");
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", endSlide);
  }, []);

  const onMove = useCallback((e: MouseEvent) => {
    if (!draggingRef.current) return;
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const max = Math.max(0, (track.clientWidth || 0) - KNOB_W);
    const proposed = e.clientX - rect.left - KNOB_W / 2;
    const nx = Math.min(max, Math.max(0, proposed));
    slideXRef.current = nx;
    setSlideX(nx);
    // Auto-complete as soon as threshold is crossed
    const maxTravel = Math.max(
      0,
      (trackRef.current?.clientWidth || 0) - KNOB_W
    );
    if (!completedOnceRef.current && nx >= maxTravel * COMPLETE_AT) {
      completedOnceRef.current = true;
      slideXRef.current = maxTravel;
      setSlideX(maxTravel);
      setStatus("complete");
      try {
        (window.navigator as any)?.vibrate?.(10);
      } catch { }
      draggingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", endSlide);
      onCompleteRef.current();
    }
  }, []);

  const endSlide = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      draggingRef.current = false;
      return;
    }
    const maxTravel = Math.max(0, (track.clientWidth || 0) - KNOB_W);
    const completed = slideXRef.current >= maxTravel * COMPLETE_AT;
    if (completed) {
      slideXRef.current = maxTravel;
      setSlideX(maxTravel);
      setStatus("complete");
      try {
        (window.navigator as any)?.vibrate?.(10);
      } catch { }
      onCompleteRef.current();
    } else {
      slideXRef.current = 0;
      setSlideX(0);
      setStatus("idle");
    }
    draggingRef.current = false;
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", endSlide);
  }, [onMove, onComplete]);

  const onTrackTap = useCallback(() => {
    // No-op: disable tap-to-bounce behavior
  }, []);

  const startShimmer = useCallback(() => setShimmer(true), []);
  const stopShimmer = useCallback(() => setShimmer(false), []);

  useEffect(() => {
    startShimmer();
    const t = setTimeout(() => stopShimmer(), 1600);
    return () => {
      clearTimeout(t);
      // Safety cleanup in case component unmounts mid-drag
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", endSlide);
      document.removeEventListener("touchmove", onMoveTouch as any);
      document.removeEventListener("touchend", endSlideTouch as any);
    };
  }, [startShimmer, stopShimmer]);

  // Touch events for mobile responsiveness
  const beginSlideTouch = useCallback(
    (e: React.TouchEvent<HTMLSpanElement>) => {
      e.preventDefault();
      draggingRef.current = true;
      setStatus("dragging");
      document.addEventListener("touchmove", onMoveTouch, { passive: false });
      document.addEventListener("touchend", endSlideTouch);
    },
    []
  );

  const onMoveTouch = useCallback((e: TouchEvent) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const touch = e.touches[0];
    if (!touch) return;
    const maxTravel = Math.max(0, (track.clientWidth || 0) - KNOB_W);
    const proposed = touch.clientX - rect.left - KNOB_W / 2;
    const nx = Math.min(maxTravel, Math.max(0, proposed));
    slideXRef.current = nx;
    setSlideX(nx);
    if (!completedOnceRef.current && nx >= maxTravel * COMPLETE_AT) {
      completedOnceRef.current = true;
      slideXRef.current = maxTravel;
      setSlideX(maxTravel);
      setStatus("complete");
      try {
        (window.navigator as any)?.vibrate?.(10);
      } catch { }
      draggingRef.current = false;
      document.removeEventListener("touchmove", onMoveTouch);
      document.removeEventListener("touchend", endSlideTouch);
      onCompleteRef.current();
    }
  }, []);

  const endSlideTouch = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      draggingRef.current = false;
      return;
    }
    const maxTravel = Math.max(0, (track.clientWidth || 0) - KNOB_W);
    const completed = slideXRef.current >= maxTravel * COMPLETE_AT;
    if (completed) {
      slideXRef.current = maxTravel;
      setSlideX(maxTravel);
      setStatus("complete");
      try {
        (window.navigator as any)?.vibrate?.(10);
      } catch { }
      onCompleteRef.current();
    } else {
      slideXRef.current = 0;
      setSlideX(0);
      setStatus("idle");
    }
    draggingRef.current = false;
    document.removeEventListener("touchmove", onMoveTouch);
    document.removeEventListener("touchend", endSlideTouch);
  }, [onComplete]);

  const reset = useCallback(() => {
    completedOnceRef.current = false;
    slideXRef.current = 0;
    setSlideX(0);
    setStatus("idle");
  }, []);

  // label fade based on progress (0..1)
  const labelOpacity = useCallback(() => {
    const max = Math.max(0, (trackRef.current?.clientWidth || 0) - KNOB_W);
    if (max <= 0) return 1;
    const progress = Math.min(1, Math.max(0, slideX / max));
    return Math.max(0, Math.min(1, 1 - progress * 1.2));
  }, [slideX, KNOB_W]);

  return {
    trackRef,
    slideX,
    status,
    beginSlide,
    beginSlideTouch,
    onTrackTap,
    shimmer,
    startShimmer,
    stopShimmer,
    KNOB_W,
    COMPLETE_AT,
    reset,
    labelOpacity,
  };
}
